import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Result, Spin } from 'antd';
import { useAuth } from 'hooks/useAuth';
import { usePermissions } from 'hooks/usePermissions';
import { checkAuth, logoutUser } from '../../store/authSlice';
import BlockingState from '../Error/BlockingState';
import { isModuleKeyAllowed } from '../../utils/activeModulesHrefFilter';
import { resolveActiveModulesNav } from '../../utils/resolveActiveModulesNav';
import { isPlatformAdminCompanyId, isProtectedSystemCompanyId } from '../../utils/systemCompany';
import { PERMISSIONS } from '../../utils/constants';

const ProtectedRoute = ({
  children, permission, permissions, requireAll = true, adminOnly = false,
  requireCompany = false, requireVerifiedCompany = false,
  requirePlatformAdminCompany = false, requireSystemMarkbaseCompany = false,
  moduleKey, redirectTo = '/login',
}) => {
  const { isAuthenticated, loading, user, company, bootstrapStatus, bootstrapError, contextReady } = useAuth();
  const { hasPermission, hasPermissions, isAdmin, isSuperAdmin } = usePermissions();
  const dispatch = useDispatch();
  const location = useLocation();

  if (loading) {
    return <Result icon={<Spin size="large" />} title="Открываем рабочее пространство" />;
  }

  // A failed lookup is recoverable. It does not establish an empty company list.
  if (bootstrapStatus === 'error') {
    return (
      <Result
        status="warning"
        title="Не удалось загрузить рабочее пространство"
        subTitle={bootstrapError || 'Проверьте соединение и повторите попытку.'}
        extra={[
          <Button type="primary" key="retry" onClick={() => dispatch(checkAuth())}>Повторить</Button>,
          <Button key="logout" onClick={() => dispatch(logoutUser())}>Выйти из аккаунта</Button>,
        ]}
      />
    );
  }

  if (isAuthenticated && user && contextReady) {
    if (user.is_active === false) return <Navigate to="/account-suspended" replace />;

    const hasCompany = Boolean(company?.id);
    const setupPath = ['/company/create', '/company/join', '/company/requests'].includes(location.pathname);
    const needsCompany = requireCompany || requireVerifiedCompany || requirePlatformAdminCompany
      || requireSystemMarkbaseCompany || adminOnly || moduleKey || permission || permissions?.length;

    if (!hasCompany && needsCompany && !setupPath) {
      return <Navigate to="/company/create" replace />;
    }

    const verificationStatus = company?.verification_status || company?.verificationStatus;
    const isVerified = company?.verified === true || verificationStatus === 'verified' || Boolean(company?.verified_at);
    if (requireVerifiedCompany && hasCompany && !isVerified) {
      return <BlockingState type="VERIFICATION_REQUIRED" />;
    }

    if (requireSystemMarkbaseCompany && isSuperAdmin() && hasCompany && !isProtectedSystemCompanyId(company.id)) {
      return <BlockingState type="SYSTEM_MARKBASE_COMPANY_REQUIRED" />;
    }

    const needsPlatformCompany = requirePlatformAdminCompany
      || (!requireSystemMarkbaseCompany && permission === PERMISSIONS.ADMIN_ACCESS && isSuperAdmin());
    if (needsPlatformCompany && isSuperAdmin() && hasCompany && !isPlatformAdminCompanyId(company.id)) {
      return <BlockingState type="PLATFORM_COMPANY_REQUIRED" />;
    }

    if (moduleKey) {
      const activeModules = resolveActiveModulesNav(company, { isSuperAdmin: isSuperAdmin() });
      if (!isModuleKeyAllowed(moduleKey, activeModules)) return <BlockingState type="MODULE_DISABLED" />;
    }

    if (adminOnly && !isAdmin()) return <Navigate to="/403" replace />;
    if (permission && !hasPermission(permission)) return <Navigate to="/403" replace />;
    if (permissions?.length && !hasPermissions(permissions, requireAll)) return <Navigate to="/403" replace />;
    return children;
  }

  const currentPath = location.pathname + location.search;
  const target = currentPath && currentPath !== redirectTo
    ? redirectTo + '?return_url=' + encodeURIComponent(currentPath)
    : redirectTo;
  return <Navigate to={target} replace />;
};

export default ProtectedRoute;
